<template>
  <div v-if="hasAdminOchin">
    <v-row>
      <v-col cols="6"><h1 class="font-weight-thin">Users</h1></v-col>
      <v-col cols="6" class="text-right"> <service-area-selector></service-area-selector></v-col>
    </v-row>
    <v-card outlined>
      <admin-user-import :serviceAreaId="serviceAreaId"></admin-user-import>
    </v-card>

    <v-card outlined class="mb-8 mt-5">
      <v-card-text @click="toggleShowUsers" class="toggleHeader">
        <span class="headline font-weight-thin">
          <v-icon small class="mr-3">fas {{ showUsers ? "fa-chevron-down" : "fa-chevron-right" }}</v-icon>
          User List (
          <span v-if="loadingList"><v-icon>fas fa-spin fa-spinner</v-icon></span>
          <span v-else>{{ list.length }}</span> )</span
        >
      </v-card-text>
      <div v-if="showUsers">
        <v-text-field
          class="mb-4 mx-4 pt-4"
          data-cy="adminUser__filter"
          hide-details
          label="Filter Users..."
          outlined
          prepend-inner-icon="fa-search"
          single-line
          v-model="search"
          style="max-width: 50%"
          clearable
          dense
        ></v-text-field>
        <v-data-table
          :headers="headerList"
          :items="userList"
          :loading="loadingList"
          :search="search"
          :custom-filter="customFilter"
          data-cy="adminUser__table"
          must-sort
          v-if="list"
          dense
        >
          <template v-slot:header.upnDisplay="{ header }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ header.text }}
                  <v-icon small class="mx-1" role="button" tabindex="0" aria-label="More info about User Principal Name"
                    >fas fa-info-circle</v-icon
                  >
                </span>
              </template>
              <span>The UPN ends in "@community-health.org" and is used for login.</span>
              <br />
              <span
                >If you do not see a UPN for an active user that has been imported and needs to log into Continuum,
                create a TOOLS ticket with a Continuum component.</span
              >
            </v-tooltip>
          </template>

          <template v-slot:item="{ item }">
            <tr>
              <td>
                <span role="img" :aria-label="item.active ? 'Active' : 'Inactive'">
                  <v-icon v-if="item.active" small color="primary">far fa-check-circle</v-icon>
                  <v-icon v-else small>fas fa-times</v-icon>
                </span>
              </td>
              <td>{{ item.serviceArea.name }}</td>
              <td :data-cy="`role-${item.username}`">{{ item.role }}</td>
              <td data-cy="adminUserTable-username">
                {{ item.upnDisplay }}
              </td>
              <td data-cy="adminUserTable-displayName">{{ item.displayName }}</td>
              <td data-cy="adminUserTable-email">{{ item.email }}</td>
              <td>{{ item.dateCreated }}</td>
              <td>
                <v-btn
                  :data-cy="`edit-icon-${item.username}`"
                  @click="showEditForm(item)"
                  class="ma-1"
                  color="primary"
                  outlined
                  x-small
                  >Edit</v-btn
                >
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <admin-user-edit v-model="showEdit"></admin-user-edit>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import AdminUserEdit from "./AdminUserEdit";
import AdminUserImport from "./AdminUserImport";
import ServiceAreaSelector from "@/components/ServiceAreaSelector";

const DEFAULT_SERVICE_AREA_ID = 848;

export default {
  async created() {
    if (!this.$route.query.serviceArea) {
      // push default and fetch the default explicitly because $route may not update synchronously
      this.$router.push({ query: { serviceArea: DEFAULT_SERVICE_AREA_ID } });
      await this.fetchList(DEFAULT_SERVICE_AREA_ID);
    } else {
      await this.fetchList(this.serviceAreaId);
    }
  },
  components: {
    AdminUserEdit,
    AdminUserImport,
    ServiceAreaSelector,
  },
  computed: {
    serviceAreaId() {
      return parseInt(this.$route.query.serviceArea, 10);
    },
    userList() {
      return this.list.map(user => {
        const displayName = `${user.firstName} ${user.lastName}`;
        const upnDisplay = user.userPrincipalName ? user.userPrincipalName : user.username;

        return {
          ...user,
          displayName,
          upnDisplay,
          // Add precomputed searchable field for efficient O(1) filtering
          _searchable: [displayName, user.firstName, user.lastName, user.email, upnDisplay, user.role]
            .filter(Boolean)
            .join(" ")
            .toLowerCase(),
        };
      });
    },
    ...mapGetters({
      hasAdminOchin: "session/hasAdminOchin",
    }),
    ...mapState({
      edit: state => state.admin.user.edit,
      list: state => state.admin.user.list,
      loadingList: state => state.admin.user.loadingList,
    }),
  },
  data() {
    return {
      headerList: [
        {
          text: "",
          value: "_searchable",
          sortable: false,
          class: "d-none",
        },
        {
          text: "Active",
          value: "active",
        },
        {
          text: "Service Area",
          value: "serviceArea.name",
        },
        {
          text: "Role",
          value: "role",
        },
        {
          text: "User Principal Name (Used for Login)",
          value: "upnDisplay",
        },
        {
          text: "Name",
          value: "lastName",
        },
        {
          text: "Email",
          value: "email",
        },
        {
          text: "Date Created",
          value: "dateCreated",
        },
        {
          sortable: false,
          text: "Edit",
        },
      ],
      search: null,
      showEdit: false,
      showImport: false,
      showUsers: true,
    };
  },
  methods: {
    customFilter(value, search) {
      // Use precomputed _searchable field for O(1) filtering per invocation
      if (!search) return true;
      return value && typeof value === "string" && value.includes(search.toLowerCase());
    },
    showCreateForm() {
      this.showImport = true;
    },
    showEditForm(user) {
      this.setEdit(Object.assign({}, user));
      this.showEdit = true;
    },
    toggleShowUsers() {
      this.showUsers = !this.showUsers;
    },
    ...mapActions({
      fetchList: "admin/user/fetchListByServiceArea",
    }),
    ...mapMutations({
      setEdit: "admin/user/setEdit",
    }),
  },
  watch: {
    serviceAreaId(value) {
      this.fetchList(value);
    },
  },
};
</script>

<style scoped>
.toggleHeader {
  cursor: pointer;
}
</style>
